import { LoaderCircle, LogOut, Menu, Target, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { appScreens } from '@/features/tracker/constants';
import {
  formatDateForHeading,
  formatDateForSummary,
  formatNumber,
  getErrorMessage,
  readScreenFromLocation,
} from '@/features/tracker/helpers';
import { HistoryScreen } from '@/features/tracker/history-screen';
import { GoalsScreen } from '@/features/tracker/goals-screen';
import { LibraryScreen } from '@/features/tracker/library-screen';
import { LogScreen } from '@/features/tracker/log-screen';
import { TodayScreen } from '@/features/tracker/today-screen';
import type { AppScreen } from '@/features/tracker/types';
import { authClient } from '@/lib/auth-client';
import { getMcpAuthorizationContinuation } from '@/lib/mcp-login';
import { trpc } from '@/lib/trpc';

type AuthMode = 'sign-in' | 'sign-up';

const navigationMenuScreenIds: AppScreen[] = ['today', 'history', 'library', 'log'];

export default function App() {
  const [mode, setMode] = useState<AuthMode>('sign-up');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigationMenuOpen, setIsNavigationMenuOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState<AppScreen>(() => readScreenFromLocation());

  const now = new Date();
  const todayDate = formatDateForSummary(now);
  const todayHeading = formatDateForHeading(now);
  const utils = trpc.useUtils();
  const sessionState = authClient.useSession();
  const hasSession = sessionState.data !== null && sessionState.data !== undefined;
  const mcpAuthorizationContinuation = getMcpAuthorizationContinuation(window.location.search);
  const viewer = trpc.auth.session.useQuery(undefined, {
    enabled: hasSession,
    retry: false,
  });
  const todaySummary = trpc.today.summary.useQuery(
    {
      date: todayDate,
    },
    {
      enabled: hasSession,
      retry: false,
    },
  );

  useEffect(() => {
    function syncScreenFromHash() {
      setActiveScreen(readScreenFromLocation());
    }

    window.addEventListener('hashchange', syncScreenFromHash);
    syncScreenFromHash();

    return () => window.removeEventListener('hashchange', syncScreenFromHash);
  }, []);

  useEffect(() => {
    setIsNavigationMenuOpen(false);
  }, [activeScreen]);

  useEffect(() => {
    if (!hasSession || !mcpAuthorizationContinuation) {
      return;
    }

    window.location.replace(mcpAuthorizationContinuation);
  }, [hasSession, mcpAuthorizationContinuation]);

  useEffect(() => {
    if (!isNavigationMenuOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;

    function closeMenuOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsNavigationMenuOpen(false);
      }
    }

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeMenuOnEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', closeMenuOnEscape);
    };
  }, [isNavigationMenuOpen]);

  async function refreshAuthenticatedState() {
    await sessionState.refetch();
    await utils.auth.session.invalidate();
    await utils.today.summary.invalidate({ date: todayDate });
  }

  function navigateToScreen(screen: AppScreen) {
    setIsNavigationMenuOpen(false);

    if (screen === activeScreen) {
      return;
    }

    window.location.hash = screen;
  }

  function selectAuthMode(nextMode: AuthMode) {
    setMode(nextMode);
    setFormError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    try {
      const authResult =
        mode === 'sign-up'
          ? await authClient.signUp.email({
              name: name.trim(),
              email: email.trim(),
              password,
            })
          : await authClient.signIn.email({
              email: email.trim(),
              password,
            });

      if (authResult.error) {
        throw authResult.error;
      }

      if (mcpAuthorizationContinuation) {
        window.location.assign(mcpAuthorizationContinuation);
        return;
      }

      await refreshAuthenticatedState();
      setPassword('');
    } catch (error) {
      if (mcpAuthorizationContinuation) {
        const session = await authClient.getSession().catch(() => null);

        if (session?.data) {
          window.location.assign(mcpAuthorizationContinuation);
          return;
        }
      }

      const message = getErrorMessage(error);

      setFormError(
        mode === 'sign-in' ? `${message} New here? Choose Create account first.` : message,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSignOut() {
    setFormError(null);
    setIsSubmitting(true);
    setIsNavigationMenuOpen(false);

    try {
      await authClient.signOut();
      await sessionState.refetch();
      await utils.auth.session.invalidate();
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  const isBooting = sessionState.isPending;
  const isProtectedStateLoading = hasSession && (viewer.isPending || todaySummary.isPending);
  const activeUser = viewer.data?.user ?? null;
  const summary = todaySummary.data;
  const caloriesLogged = summary?.totals.calories ?? 0;
  const calorieGoal = summary?.goal?.calories ?? null;
  const calorieStatusLabel =
    calorieGoal === null
      ? `${formatNumber(caloriesLogged)} cal logged`
      : `${formatNumber(caloriesLogged)} / ${formatNumber(calorieGoal)} cal`;
  const navigationMenuScreens = navigationMenuScreenIds
    .map((screenId) => appScreens.find((screen) => screen.id === screenId))
    .filter((screen): screen is (typeof appScreens)[number] => Boolean(screen));

  return (
    <main className="app-shell fixed inset-0 overflow-hidden px-4 text-foreground sm:px-6">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {isBooting || isProtectedStateLoading ? (
          <Card className="my-auto flex items-center gap-3">
            <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading your tracker…</p>
          </Card>
        ) : hasSession && activeUser ? (
          <>
            <header className="app-status-bar shrink-0 bg-background/95 backdrop-blur">
              <section className="flex min-h-9 items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-medium leading-4 text-foreground">
                    {calorieStatusLabel}
                  </p>
                  <p className="text-[10px] leading-3 text-muted-foreground">{todayHeading}</p>
                </div>
                <button
                  aria-expanded={isNavigationMenuOpen}
                  aria-label="Open menu"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border bg-background text-foreground transition-colors hover:bg-accent"
                  disabled={isSubmitting}
                  onClick={() => setIsNavigationMenuOpen((isOpen) => !isOpen)}
                  type="button"
                >
                  <Menu className="h-4 w-4" />
                </button>
              </section>

              {formError ? (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </p>
              ) : null}
            </header>

            {isNavigationMenuOpen ? (
              <div
                aria-modal="true"
                className="fixed inset-0 z-50 flex items-end bg-foreground/30 nav-sheet-backdrop"
                role="dialog"
              >
                <button
                  aria-label="Close menu"
                  className="absolute inset-0 h-full w-full cursor-default"
                  onClick={() => setIsNavigationMenuOpen(false)}
                  type="button"
                />
                <section className="nav-sheet-panel relative w-full rounded-t-lg border border-border bg-card px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3 text-card-foreground shadow-[0_-12px_40px_rgba(0,0,0,0.18)]">
                  <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-border" />
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Menu</p>
                      <h2 className="mt-1 text-lg font-semibold">Go to</h2>
                    </div>
                    <button
                      aria-label="Close menu"
                      className="inline-flex h-9 w-9 items-center justify-center rounded border border-border bg-background transition-colors hover:bg-accent"
                      onClick={() => setIsNavigationMenuOpen(false)}
                      type="button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <nav aria-label="Main menu">
                    <ul className="divide-y divide-border border-y border-border">
                      {navigationMenuScreens.map((screen) => {
                        const Icon = screen.icon;
                        const isActive = screen.id === activeScreen;
                        const label = screen.id === 'log' ? 'Logging' : screen.label;

                        return (
                          <li key={screen.id}>
                            <button
                              className={`flex min-h-14 w-full items-center gap-3 px-1 text-left text-sm font-medium transition-colors ${
                                isActive
                                  ? 'text-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                              onClick={() => navigateToScreen(screen.id)}
                              type="button"
                            >
                              <Icon className="h-5 w-5" />
                              <span className="flex-1">{label}</span>
                              {isActive ? (
                                <span className="text-xs font-medium text-foreground">Open</span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>

                  <div className="mt-4 grid gap-2">
                    <button
                      className="flex min-h-11 w-full items-center gap-3 rounded-md px-1 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
                      disabled={isSubmitting}
                      onClick={() => navigateToScreen('goals')}
                      type="button"
                    >
                      <Target className="h-4 w-4" />
                      Goals
                    </button>
                    <button
                      className="flex min-h-11 w-full items-center gap-3 rounded-md px-1 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
                      disabled={isSubmitting}
                      onClick={handleSignOut}
                      type="button"
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </button>
                  </div>
                </section>
              </div>
            ) : null}

            <section
              className={`app-scroll-content min-h-0 flex flex-1 flex-col overscroll-contain ${
                activeScreen === 'log' ? 'overflow-hidden' : 'overflow-y-auto'
              }`}
            >
              {activeScreen === 'today' ? (
                <TodayScreen
                  onNavigateToLog={() => navigateToScreen('log')}
                  summary={summary}
                  summaryError={
                    todaySummary.error ? getErrorMessage(todaySummary.error) : undefined
                  }
                />
              ) : null}
              {activeScreen === 'goals' ? (
                <GoalsScreen goal={summary?.goal ?? null} todayDate={todayDate} />
              ) : null}
              {activeScreen === 'history' ? <HistoryScreen todayDate={todayDate} /> : null}
              {activeScreen === 'library' ? <LibraryScreen todayDate={todayDate} /> : null}
              {activeScreen === 'log' ? (
                <LogScreen
                  onNavigateToToday={() => navigateToScreen('today')}
                  storageKey={`calorie-tracker:log-screen:${activeUser.id}`}
                  todayDate={todayDate}
                />
              ) : null}
            </section>
          </>
        ) : (
          <div className="my-auto space-y-4">
            <section className="space-y-3">
              <h1 className="text-3xl font-semibold">Calorie Tracker</h1>
              <p className="text-sm leading-6 text-muted-foreground">
                {mcpAuthorizationContinuation
                  ? 'Sign in to connect Calorie Tracker, then you’ll return to ChatGPT.'
                  : 'Sign in to log food, review drafts, and keep today’s totals current.'}
              </p>
            </section>

            <Card className="space-y-4">
              <div className="grid grid-cols-2 rounded-md bg-secondary p-1">
                <button
                  className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                    mode === 'sign-in'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => selectAuthMode('sign-in')}
                  type="button"
                >
                  Sign in
                </button>
                <button
                  className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                    mode === 'sign-up'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => selectAuthMode('sign-up')}
                  type="button"
                >
                  Create account
                </button>
              </div>

              <form className="space-y-3" onSubmit={handleSubmit}>
                {mode === 'sign-up' ? (
                  <label className="grid gap-2 text-sm">
                    <span className="font-medium">Name</span>
                    <input
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-ring/20"
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Grant"
                      required
                      type="text"
                      value={name}
                    />
                  </label>
                ) : null}

                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Email</span>
                  <input
                    autoComplete="email"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-ring/20"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                    type="email"
                    value={email}
                  />
                </label>

                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Password</span>
                  <input
                    autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-ring/20"
                    minLength={8}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="At least 8 characters"
                    required
                    type="password"
                    value={password}
                  />
                </label>

                {formError ? (
                  <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {formError}
                  </p>
                ) : null}

                {sessionState.error ? (
                  <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {getErrorMessage(sessionState.error)}
                  </p>
                ) : null}

                <Button className="w-full" disabled={isSubmitting} size="lg" type="submit">
                  {isSubmitting ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Working
                    </>
                  ) : mode === 'sign-up' ? (
                    'Create account'
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </Card>
          </div>
        )}
      </div>
    </main>
  );
}
